// src/pages/Maxsulotqosh.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import "./Maxsulotqosh.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { useSklad } from "../SkladContext";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";

const PAGE_SIZE = 24;
const RENDER_BATCH = 24;
const MAX_RENDERED = 5240;

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "") || "http://";

const API_ORIGIN = (() => {
  try {
    const u = new URL(BASE_URL);
    return u.origin;
  } catch {
    return "http://localhost:8000";
  }
})();

/* ============ Decimal helpers ============ */
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const trimTrailingZeros = (v) => {
  const s = normDecStr(v);
  if (!s) return "";
  if (!s.includes(".")) return s;
  return s.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "").replace(/\.$/u, "");
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (v, { min = 0, max = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " so'm";
const fmtQty = (v, { min = 0, max = 3 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max });

const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const hasNext = (res) => Boolean(res?.data?.next);

/* ============ Series sort helpers (YT-0001) ============ */
const seriesKey = (s) => {
  const str = String(s || "").trim().toUpperCase();
  const m = str.match(/^([A-Z]+)[\s\-_/]*0*(\d+)\s*$/);
  if (m) return { prefix: m[1], num: parseInt(m[2], 10) || 0, raw: str };
  return { prefix: "", num: Number.MAX_SAFE_INTEGER, raw: str };
};
const cmpSeries = (a, b) => {
  const A = seriesKey(a?.seriyasi);
  const B = seriesKey(b?.seriyasi);
  if (A.prefix !== B.prefix) return A.prefix.localeCompare(B.prefix);
  if (A.num !== B.num) return A.num - B.num;
  return A.raw.localeCompare(B.raw);
};

/* ============ DEDUPE helpers ============ */
const dedupeById = (arr) => {
  const m = new Map();
  for (const x of arr || []) {
    const id = x?.id;
    const key = id != null ? String(id) : `${x?.sklad || ""}::${x?.seriyasi || ""}::${x?.nom || ""}`;
    if (!m.has(key)) m.set(key, x);
  }
  return Array.from(m.values());
};

const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "")
    .replace(/[`'"]/g, "");

const sellerLabel = (s) =>
  s ? `${(s.ismi || "").trim() || "Nomsiz"}${s.Dokonnomi ? ` - ${s.Dokonnomi}` : ""}` : "";

const normalizeEditComparable = (source = {}) => ({
  category: String(source.category || ""),
  brand: String(source.brand || ""),
  warehouse: String(source.warehouse || ""),
  name: String(source.name || "").trim(),
  quantity: trimTrailingZeros(source.quantity ?? ""),
  costPrice: trimTrailingZeros(source.costPrice ?? ""),
  series: String(source.series || "").trim(),
  description: String(source.description || "").trim(),
});

const hasNonImageEditChanges = (current, baseline) => {
  if (!baseline) return false;
  const normalizedCurrent = normalizeEditComparable(current);
  return Object.keys(baseline).some((key) => normalizedCurrent[key] !== baseline[key]);
};

/* ============ QR kod generatsiya ============ */
async function generateQrDataUrl(text, size = 300) {
  return QRCode.toDataURL(text, {
    width: size,
    margin: 2,
    color: { dark: "#000000", light: "#ffffff" },
  });
}

/* ============ 30x20mm label PDF yuklab olish ============
 * 30mm (eni) × 20mm (bo'yi) landscape label
 * Layout: [QR chap] | [Seriya + Narx o'ng] | [Nom pastda]
 */

// Berilgan kenglikka sig'adigan maksimal font o'lchamini topish
function fitFontSize(doc, text, maxW, startSize, minSize, fontStyle = "bold") {
  let size = startSize;
  doc.setFont("helvetica", fontStyle);
  doc.setFontSize(size);
  while (size > minSize && doc.getTextWidth(text) > maxW) {
    size -= 0.5;
    doc.setFontSize(size);
  }
  return size;
}

// Label PDF hujjatini yasaydi — yuklab olish ham, chop etish ham shu bitta chizmadan
async function buildLabelPdf(series, name, price) {
  const qrDataUrl = await generateQrDataUrl(series, 512);

  const priceNum = price != null ? Number(price) : 0;
  const priceDisplay = priceNum > 0
    ? Math.round(priceNum).toLocaleString("uz-UZ")
    : "";

  const W = 30, H = 20;
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [H, W],
  });

  const pageW = doc.internal.pageSize.getWidth();   // 30
  const pageH = doc.internal.pageSize.getHeight();  // 20

  // Fon oq
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, pageH, "F");

  // ── QR rasm (chap, 1mm margin) ──
  const qrSize = pageH - 6;    // 14mm
  const qrX = 1, qrY = 1;
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

  // ── O'ng ustun ──
  const textX = qrX + qrSize + 1;         // 16mm
  const textW = pageW - textX - 1;        // 13mm
  doc.setTextColor(0, 0, 0);

  // Seriya — bir qatorda sig'adigan qilib
  const seriesSize = fitFontSize(doc, series, textW, 7, 4, "bold");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(seriesSize);
  doc.text(series, textX, 4.5);

  // Narx — bir qatorda sig'adigan qilib (priceDisplay ko'rsatish uchun)
  if (priceDisplay) {
    const priceSize = fitFontSize(doc, priceDisplay, textW, 11, 5, "bold");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(priceSize);
    doc.text(priceDisplay, textX, 4.5 + priceSize * 0.45 + 1.5);
  }

  // ── Nom (pastki, ajratgich chiziq bilan) ──
  if (name) {
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(1, pageH - 4.6, pageW - 1, pageH - 4.6);

    doc.setFont("helvetica", "normal");
    // Nom uchun kenglikka sig'adigan font
    const nameSize = fitFontSize(doc, name, pageW - 2, 5, 3, "normal");
    doc.setFontSize(nameSize);
    doc.setTextColor(30, 30, 30);
    doc.text(name, 1, pageH - 1.6);
  }

  return doc;
}

async function downloadQrPdf(series, name, price) {
  const doc = await buildLabelPdf(series, name, price);
  const safeName = series.replace(/[^a-zA-Z0-9_-]/g, "_");
  doc.save(`label_${safeName}.pdf`);
}

/* ============ 30x20mm labelni brauzerdan to'g'ridan-to'g'ri chop etish ============
 * HTML emas, tayyor PDF chop etiladi: Chrome PDF'ga sana/sarlavha/sayt manzili (headers and
 * footers) qo'shmaydi va sahifa aniq 30x20mm bo'ladi — yuklab olinadigan PDF bilan bir xil.
 */
async function printQrLabel(series, name, price) {
  const doc = await buildLabelPdf(series, name, price);
  const url = URL.createObjectURL(doc.output("blob"));
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:-10000px;bottom:-10000px;width:60mm;height:40mm;border:0";
  iframe.src = url;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        // Brauzer iframe ichidagi PDF'ni chop etishga ruxsat bermasa — yangi oynada ochamiz
        window.open(url, "_blank");
      }
      setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
    }, 200);
  };
  document.body.appendChild(iframe);
}

const extractApiErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  if (typeof data === "object") {
    for (const value of Object.values(data)) {
      const message = extractApiErrorMessage(value, "");
      if (message) return message;
    }
  }
  return fallback;
};

/* ============ ComboSelect (autocomplete input) ============ */
function ComboSelect({ value, onChange, items, placeholder, disabled = false, getLabel }) {
  const labelOf = getLabel || ((it) => it.nom || it.name || "");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const selectedName = useMemo(() => {
    if (!value) return "";
    const it = items.find((x) => String(x.id) === String(value));
    return it ? labelOf(it) : "";
  }, [value, items, labelOf]);

  const suggestions = useMemo(() => {
    if (!focused) return [];
    const q = text.trim().toLowerCase();
    if (!q) return items.slice(0, 40);
    return items.filter((x) => labelOf(x).toLowerCase().includes(q));
  }, [items, text, focused, labelOf]);

  useEffect(() => {
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
        setText("");
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const handleFocus = () => {
    setFocused(true);
    setText("");
    setOpen(true);
  };

  const handleChange = (e) => {
    setText(e.target.value);
    if (!e.target.value) onChange("");
    setOpen(true);
  };

  const handleSelect = (item) => {
    onChange(String(item.id));
    setText("");
    setFocused(false);
    setOpen(false);
  };

  return (
    <div className="combo-wrap" ref={wrapRef}>
      <input
        type="text"
        className="combo-input"
        placeholder={placeholder}
        value={focused ? text : selectedName}
        onFocus={handleFocus}
        onChange={handleChange}
        disabled={disabled}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="combo-dropdown">
          {value && (
            <button
              type="button"
              className="combo-option combo-clear"
              onMouseDown={() => { onChange(""); setText(""); setFocused(false); setOpen(false); }}
            >
              — Tozalash
            </button>
          )}
          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`combo-option${String(item.id) === String(value) ? " active" : ""}`}
              onMouseDown={() => handleSelect(item)}
            >
              {labelOf(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SellerSelect = ({ value, onChange, sellers, placeholder = "Tanlang", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const selected = sellers.find((s) => String(s.id) === String(value));
  const filtered = useMemo(() => {
    const key = (q || "").trim().toLowerCase();
    if (!key) return sellers;
    return sellers.filter((s) => {
      const nm = (s.ismi || "").toLowerCase();
      const dn = (s.Dokonnomi || "").toLowerCase();
      const tel = `${s.telraqam1 || ""} ${s.telraqam2 || ""}`.toLowerCase();
      return nm.includes(key) || dn.includes(key) || tel.includes(key);
    });
  }, [sellers, q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className={`seller-select ${open ? "open" : ""}`} ref={ref}>
      <button
        type="button"
        className="seller-btn"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className={selected ? "has-value" : "placeholder"}>
          {selected ? sellerLabel(selected) : placeholder}
        </span>
        <span className="chev">▾</span>
      </button>
      {open && (
        <div className="seller-panel">
          <input
            type="text"
            className="seller-search"
            placeholder="Haridor qidirish..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="seller-list">
            {filtered.length === 0 && <div className="seller-empty">Topilmadi</div>}
            {filtered.map((s) => (
              <button
                type="button"
                key={s.id}
                className="seller-item"
                onClick={() => {
                  onChange(String(s.id));
                  setOpen(false);
                }}
              >
                {sellerLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Maxsulotqosh() {
  const warehouseAccess = useMemo(() => getWarehouseAccess(), []);
  const { allAccess, allowedIds } = warehouseAccess;

  // reference ro'yxatlar
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [sellers, setSellers] = useState([]);

  // server paging
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // qidiruv
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const searchTimer = useRef(null);

  // filtrlar
  const { activeSklad } = useSklad();
  const [warehouse, setWarehouse] = useState(allAccess ? activeSklad : "all");
  useEffect(() => {
    if (allAccess) setWarehouse(activeSklad);
  }, [activeSklad, allAccess]);
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  const [stockFilter, setStockFilter] = useState("available"); // available | out | all
  const [sortSeries, setSortSeries] = useState("asc"); // asc | desc

  // view mode
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  // edit mode
  const [editingId, setEditingId] = useState(null);
  const [seriesNonce, setSeriesNonce] = useState(0);

  // form
  const [form, setForm] = useState({
    category: "",
    brand: "",
    warehouse: "",
    name: "",
    quantity: "",
    sourceSeller: "",
    costPrice: "",
    series: "km-",
    qrKod: "",
    description: "",
    editNote: "",
    image: null,
    imagePreview: null,
    prices: {}, // { [narx_turi_id]: string decimal }
  });

  // QR modal state
  const [qrModal, setQrModal] = useState(null); // { series, name }
  const qrCanvasRef = useRef(null);

  // mobile drawers
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // add-stock modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [addQty, setAddQty] = useState("");
  const [addSeller, setAddSeller] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);

  // rasmni almashtirish (PATCH) uchun
  const imageInputRef = useRef(null);
  const [editImageFor, setEditImageFor] = useState(null);
  const editImageForRef = useRef(null);
  const editBaselineRef = useRef(null);

  // list so‘rovlarini bekor qilish uchun
  const listAbortRef = useRef(null);

  // DOM render optimizatsiya
  const allRef = useRef([]);              // barcha kelgan mahsulotlar
  const [renderCount, setRenderCount] = useState(RENDER_BATCH);
  const [items, setItems] = useState([]); // DOM'ga chiqariladiganlar

  // preview blob URL cleanup
  const lastPreviewRef = useRef(null);
  const revokePreview = () => {
    const u = lastPreviewRef.current;
    if (u && String(u).startsWith("blob:")) {
      URL.revokeObjectURL(u);
      lastPreviewRef.current = null;
    }
  };
  useEffect(() => () => revokePreview(), []);

  /* =================== INIT: reference ro'yxatlar =================== */
  useEffect(() => {
    (async () => {
      try {
        const [c, b, w, t, s] = await Promise.all([
          api.get("maxsulot-kategoriyasi/"),
          api.get("brend/"),
          api.get("sklad/"),
          api.get("narx-turi/"),
          api.get("sotuvchi/"),
        ]);
        const allWarehouses = Array.isArray(w.data) ? w.data : w.data.results || [];
        const visibleWarehouses = allAccess
          ? allWarehouses
          : allWarehouses.filter((item) => allowedIds.includes(Number(item.id)));
        setCategories(Array.isArray(c.data) ? c.data : c.data.results || []);
        setBrands(Array.isArray(b.data) ? b.data : b.data.results || []);
        setWarehouses(visibleWarehouses);
        setPriceTypes(Array.isArray(t.data) ? t.data : t.data.results || []);
        setSellers(Array.isArray(s.data) ? s.data : s.data.results || []);

        // Cashier uchun: allowed sklad auto-select
        if (!allAccess && allowedIds.length === 1) {
          const sklad = visibleWarehouses.find((x) => Number(x.id) === allowedIds[0]);
          const prefix = sklad?.seriya_prefix || "km-";
          setWarehouse(String(allowedIds[0]));
          setForm((prev) => ({ ...prev, warehouse: String(allowedIds[0]), series: prefix }));
        } else if (!allAccess && allowedIds.length > 1) {
          setWarehouse("all");
        }
      } catch (e) {
        console.log("Init load error", e?.response?.status, e?.response?.data);
        window.alert("Ro'yxatlarni yuklashda xatolik yuz berdi.");
      }
    })();
  }, [allAccess, allowedIds]);

  const resetList = useCallback(() => {
    setPage(1);
    setHasMore(true);
    allRef.current = [];
    setRenderCount(RENDER_BATCH);
    setItems([]);
  }, []);

  // filtr/sort o'zgarsa: 1-sahifadan qayta yuklash
  useEffect(() => {
    resetList();
  }, [warehouse, category, brand, stockFilter, sortSeries, resetList]);

  /* =================== PAGED LIST LOAD =================== */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try { listAbortRef.current?.abort(); } catch {}
      const controller = new AbortController();
      listAbortRef.current = controller;

      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("page_size", String(PAGE_SIZE));

        if ((q || "").trim()) params.set("search", q.trim());

        // filtrlar (backend qo'llasa ishlaydi; qo'llamasa fallback bor)
        if (warehouse !== "all") params.set("sklad", String(warehouse));
        if (category !== "all") params.set("kategoriya " , String(category)); // NOTE: agar backend "kategoriya" bo'lsa pastdagi satrni ishlating
        // ↑ agar sizda backend param nomi "kategoriya" bo'lsa, yuqoridagi "kategoriya " dagi bo'sh joyni o'chiring.
        if (category !== "all") params.set("kategoriya", String(category));
        if (brand !== "all") params.set("brand", String(brand));

        if (stockFilter === "available") params.set("available", "1");
        if (stockFilter === "out") params.set("out", "1");

        params.set("ordering", sortSeries === "asc" ? "seriyasi" : "-seriyasi");

        const url = `maxsulot/?${params.toString()}`;
        const res = await api.get(url, { signal: controller.signal });

        const results = asArr(res);
        const next = hasNext(res);

        if (cancelled) return;

        const mergedRaw = page === 1 ? results : [...allRef.current, ...results];

        // 1) DEDUPE (dublikat muammosini kesadi)
        let rows = dedupeById(mergedRaw);

        // 2) frontend fallback filter
        if (stockFilter === "available") rows = rows.filter((p) => toNum(p?.soni) > 0);
        if (stockFilter === "out") rows = rows.filter((p) => toNum(p?.soni) <= 0);

        // 3) frontend fallback sort (seriya)
        rows = [...rows].sort((a, b) =>
          sortSeries === "asc" ? cmpSeries(a, b) : cmpSeries(b, a)
        );

        allRef.current = rows;

        const safeCount = Math.min(renderCount, rows.length, MAX_RENDERED);
        setItems(rows.slice(0, safeCount));
        setHasMore(next);
      } catch (e) {
        if (e.name === "CanceledError" || e.name === "AbortError") return;
        const st = e?.response?.status;
        console.log("LIST LOAD ERR:", st, e?.response?.data);
        if (!cancelled && st === 404) setHasMore(false);
        else if (!cancelled) window.alert("Mahsulotlarni yuklashda xato.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [page, q, renderCount, warehouse, category, brand, stockFilter, sortSeries]);

  /* =================== SEARCH (debounce) =================== */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(val.trim());
      resetList();
    }, 300);
  };
  useEffect(() => () => clearTimeout(searchTimer.current), [resetList]);

  /* =================== FORM HANDLERS =================== */
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity" || name === "costPrice") {
      setF(name, normDecStr(value));
      return;
    }
    setF(name, value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    revokePreview();
    const url = file ? URL.createObjectURL(file) : null;
    if (url) lastPreviewRef.current = url;

    setForm((prev) => ({
      ...prev,
      image: file,
      imagePreview: url,
    }));
  };

  const resetForm = useCallback(() => {
    setEditingId(null);
    editBaselineRef.current = null;
    revokePreview();
    setForm((prev) => {
      const wid = prev.warehouse;
      const sklad = warehouses.find((x) => String(x.id) === String(wid));
      const prefix = sklad?.seriya_prefix || "km-";
      return {
        category: "",
        brand: "",
        warehouse: wid,
        name: "",
        quantity: "",
        sourceSeller: "",
        costPrice: "",
        series: prefix,
        qrKod: "",
        description: "",
        editNote: "",
        image: null,
        imagePreview: null,
        prices: {},
      };
    });
    setSeriesNonce((n) => n + 1);
  }, [warehouses]);

  /* =================== Keyingi bo'sh seriya =================== */
  // Yangi mahsulot uchun seriya avtomatik (km-1608 kabi) — qo'lda band seriya yozilib qolmasin
  const fillNextSeries = useCallback(async (wid, force = false) => {
    if (!wid) return null;
    try {
      const { data } = await api.get("maxsulot/next-series/", { params: { sklad: wid } });
      const next = data?.series;
      if (!next) return null;
      setForm((prev) => {
        if (String(prev.warehouse) !== String(wid)) return prev;
        if (!force && /\d/.test(prev.series || "")) return prev; // foydalanuvchi o'zi yozgan
        return { ...prev, series: next };
      });
      return next;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!editingId && form.warehouse) fillNextSeries(form.warehouse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, form.warehouse, seriesNonce, fillNextSeries]);

  /* =================== EDIT =================== */
  const editProduct = useCallback(async (p) => {
    try {
      const { data } = await api.get(`maxsulot/${p.id}/`);
      const pricesMap = {};
      (data.narxlar || []).forEach((n) => {
        const t = priceTypes.find((x) => String(x.nom) === String(n.sarxturi));
        if (t) pricesMap[t.id] = trimTrailingZeros(n.narx ?? "");
      });

      const nextForm = {
        category: data.kategoriya ? String(data.kategoriya) : "",
        brand: data.brand ? String(data.brand) : "",
        warehouse: data.sklad ? String(data.sklad) : "",
        name: data.nom || "",
        quantity: trimTrailingZeros(String(data.soni ?? "")),
        sourceSeller: "",
        costPrice: trimTrailingZeros(String(data.tan_narxi ?? "")),
        series: data.seriyasi || "km-",
        qrKod: data.qr_kod || "",
        description: data.izoh || "",
        editNote: "",
        image: null,
        imagePreview:
          data.rasm_url ||
          (data.rasm ? (data.rasm.startsWith("http") ? data.rasm : `${API_ORIGIN}${data.rasm}`) : null),
        prices: pricesMap,
      };

      editBaselineRef.current = normalizeEditComparable(nextForm);
      setEditingId(p.id);
      setForm(nextForm);

      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileFormOpen(true);
    } catch (e) {
      console.log("EDIT LOAD ERR:", e?.response?.status, e?.response?.data);
      window.alert("Tahrirlash uchun ma'lumotlarni olishda xato.");
    }
  }, [priceTypes]);

  /* =================== CREATE/UPDATE submit =================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return window.alert("Nomi majburiy.");
    const needsEditNote = editingId && hasNonImageEditChanges(form, editBaselineRef.current);
    if (needsEditNote && !form.editNote?.trim()) {
      return window.alert("Tahrirlash izohi majburiy.");
    }
    try {
      const fd = new FormData();
      if (form.category)  fd.append("kategoriya", Number(form.category));
      if (form.brand)     fd.append("brand", Number(form.brand));
      fd.append("seriyasi", form.series || "");
      fd.append("nom", form.name || "");
      fd.append("qr_kod", form.qrKod || "");
      // Tahrirlashda sklad yuborilmaydi (ombor ko'chirish orqali). Soni faqat o'zgargan bo'lsa
      // yuboriladi, eski qiymat bilan — shu orada savdo bo'lsa server rad etadi.
      if (!editingId) {
        if (form.warehouse) fd.append("sklad", Number(form.warehouse));
        fd.append("soni", form.quantity ? form.quantity : "0");
      } else {
        const wasQty = editBaselineRef.current?.quantity ?? "";
        if (normDecStr(form.quantity || "0") !== normDecStr(wasQty || "0")) {
          fd.append("soni", form.quantity ? form.quantity : "0");
          fd.append("soni_was", wasQty || "0");
        }
      }
      fd.append("tan_narxi", form.costPrice ? form.costPrice : "0");
      // Narxlar mahsulot bilan bitta so'rovda saqlanadi; bo'sh maydon — narx o'chiriladi
      const pricesPayload = {};
      priceTypes.forEach((t) => {
        const val = normDecStr(form.prices[t.id] ?? "");
        pricesPayload[t.nom] = val !== "" && toNum(val) > 0 ? val : "";
      });
      fd.append("prices_json", JSON.stringify(pricesPayload));
      if (!editingId && form.sourceSeller) {
        fd.append("source_seller", Number(form.sourceSeller));
        const sel = sellers.find((x) => String(x.id) === String(form.sourceSeller));
        const label = sel ? `${sel.ismi || ""}${sel.Dokonnomi ? " - " + sel.Dokonnomi : ""}`.trim() : "";
        if (label) fd.append("yetkazib_beruvchi", label);
      }
      fd.append("izoh", form.description || "");
      if (editingId) fd.append("edit_note", form.editNote.trim());
      if (form.image) fd.append("rasm", form.image);

      if (editingId) {
        await api.patch(`maxsulot/${editingId}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("maxsulot/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      resetForm();
      resetList();
      setMobileFormOpen(false);

      window.alert(editingId ? "Mahsulot yangilandi ✅" : "Mahsulot saqlandi ✅");
    } catch (e2) {
      console.log("SAVE ERR:", e2?.response?.status, e2?.response?.data);
      if (e2?.response?.data?.seriyasi) {
        const busy = form.series;
        const next = !editingId ? await fillNextSeries(form.warehouse, true) : null;
        window.alert(
          next
            ? `"${busy}" seriyasi boshqa mahsulotda bor (QR kod emas, seriya band).\nKeyingi bo'sh seriya qo'yildi: ${next}\nYana "Saqlash" ni bosing.`
            : `"${busy}" seriyasi boshqa mahsulotda bor (QR kod emas, seriya band). Boshqa seriya kiriting.`
        );
        return;
      }
      window.alert(`Saqlashda xato: ${extractApiErrorMessage(e2?.response?.data, e2.message)}`);
    }
  };

  /* =================== Narx chip edit/delete =================== */
  const handleEditPrice = async (productId, n) => {
    const raw = window.prompt(`Yangi narx (${n.sarxturi}, so'm)`, String(n.narx ?? ""));
    if (raw === null) return;
    const val = normDecStr(raw);
    if (val === "") return;
    try {
      const { data } = await api.patch(`maxsulot-narxi/${n.id}/`, { narx: val });
      allRef.current = allRef.current.map((p) =>
        p.id !== productId
          ? p
          : { ...p, narxlar: (p.narxlar || []).map((x) => (x.id === n.id ? data : x)) }
      );
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
    } catch (e) {
      window.alert("Narxni tahrirlashda xato.");
      console.log(e?.response?.status, e?.response?.data);
    }
  };

  const handleDeletePrice = async (productId, priceId) => {
    if (!window.confirm("Bu narx yozuvini o‘chiramizmi?")) return;
    try {
      await api.delete(`maxsulot-narxi/${priceId}/`);
      allRef.current = allRef.current.map((p) =>
        p.id !== productId
          ? p
          : { ...p, narxlar: (p.narxlar || []).filter((x) => x.id !== priceId) }
      );
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
    } catch (e) {
      window.alert("Narxni o‘chirishda xato.");
      console.log(e?.response?.status, e?.response?.data);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ushbu mahsulot o‘chirilsinmi?")) return;
    try {
      const { data } = await api.delete(`maxsulot/${id}/`);
      allRef.current = allRef.current.filter((p) => p.id !== id);
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
      if (data?.status === "archived" && data?.detail) {
        window.alert(data.detail);
      }
    } catch (e) {
      console.log("DELETE ERR:", e?.response?.status, e?.response?.data);
      if (e?.response?.data?.detail) {
        window.alert(e.response.data.detail);
        return;
      }
      window.alert("O‘chirishda xato.");
    }
  };

  /* =================== PATCH helper (rasmni almashtirish) =================== */
  const patchProduct = async (id, payload, file) => {
    if (file) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
      fd.append("rasm", file);
      return api.patch(`maxsulot/${id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.patch(`maxsulot/${id}/`, payload);
  };

  const openImagePicker = (productId) => {
    editImageForRef.current = productId;
    setEditImageFor(productId);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
      imageInputRef.current.click();
    }
  };

  const onPickNewImage = async (e) => {
    const file = e.target.files?.[0];
    const productId = editImageForRef.current || editImageFor;
    if (!file || !productId) {
      e.target.value = "";
      return;
    }
    try {
      const { data } = await patchProduct(productId, {}, file);
      allRef.current = allRef.current.map((p) => (p.id === productId ? { ...p, ...data } : p));
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));
      if (data?.status === "archived" && data?.detail) {
        window.alert(data.detail);
      }
    } catch (err) {
      const message = extractApiErrorMessage(err?.response?.data, "Rasmni yangilashda xato.");
      window.alert(message);
      console.log(err?.response?.status, err?.response?.data);
    } finally {
      editImageForRef.current = null;
      setEditImageFor(null);
      e.target.value = "";
    }
  };

  const openAddModal = (product) => {
    setAddTarget(product);
    setAddQty("");
    setAddSeller("");
    setAddNote("");
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    if (addSubmitting) return;
    setAddModalOpen(false);
    setAddTarget(null);
  };

  const submitAddStock = async () => {
    if (!addTarget) return;
    const rawQty = normDecStr(addQty);
    if (!rawQty) return window.alert("Soni majburiy.");
    if (rawQty.includes(".")) return window.alert("Soni butun bo‘lishi kerak.");
    const qtyNum = parseInt(rawQty, 10);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) return window.alert("Soni 1 dan katta bo‘lsin.");
    if (!addSeller) return window.alert("Kimdan kelgani majburiy.");

    try {
      setAddSubmitting(true);
      const payload = {
        qty: qtyNum,
        source_seller: Number(addSeller),
      };
      const note = (addNote || "").trim();
      if (note) payload.note = note;

      const { data } = await api.post(`maxsulot/${addTarget.id}/add-stock/`, payload);

      allRef.current = allRef.current.map((p) => (p.id === data.id ? { ...p, ...data } : p));
      setItems(allRef.current.slice(0, Math.min(renderCount, allRef.current.length, MAX_RENDERED)));

      closeAddModal();
    } catch (e) {
      console.log("ADD STOCK ERR:", e?.response?.status, e?.response?.data);
      window.alert("Mahsulot qo‘shishda xato.");
    } finally {
      setAddSubmitting(false);
    }
  };

  /* =================== Infinite scroll sentinel =================== */
  const loaderRef = useRef(null);
  useEffect(() => {
    if (!loaderRef.current) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loading) return;

        const total = allRef.current.length;
        const canGrow = renderCount < Math.min(total, MAX_RENDERED);

        if (canGrow) {
          setRenderCount((c) => Math.min(c + RENDER_BATCH, total, MAX_RENDERED));
          return;
        }

        if (hasMore) setPage((p) => p + 1);
      },
      { rootMargin: "300px" }
    );

    io.observe(loaderRef.current);
    return () => io.disconnect();
  }, [loading, hasMore, renderCount]);

  const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='150' viewBox='0 0 120 150'%3E%3Crect width='120' height='150' fill='%23f0f0f0'/%3E%3Ctext x='60' y='70' text-anchor='middle' fill='%23aaa' font-size='11' font-family='sans-serif'%3ERasm%3C/text%3E%3Ctext x='60' y='86' text-anchor='middle' fill='%23aaa' font-size='11' font-family='sans-serif'%3Eyo'q%3C/text%3E%3C/svg%3E";

  const imgSrc = useCallback((p) => {
    if (p.imagePreview) return p.imagePreview;
    if (p.rasm_url) return p.rasm_url;
    if (p.rasm) return p.rasm.startsWith("http") ? p.rasm : `${API_ORIGIN}${p.rasm}`;
    return PLACEHOLDER_IMG;
  }, [PLACEHOLDER_IMG]);

  const handleImgError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = PLACEHOLDER_IMG;
  }, [PLACEHOLDER_IMG]);

  const titleText = editingId ? "Maxsulot tahrirlash" : "Maxsulot qo‘shish";

  const priceInputs = useMemo(() => (
    priceTypes.map((t) => (
      <input
        key={t.id}
        type="text"
        inputMode="decimal"
        placeholder={`${t.nom} (so'm)`}
        value={form.prices[t.id] || ""}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            prices: { ...prev.prices, [t.id]: normDecStr(e.target.value) },
          }))
        }
        onBlur={(e) =>
          setForm((prev) => ({
            ...prev,
            prices: { ...prev.prices, [t.id]: trimTrailingZeros(e.target.value) },
          }))
        }
      />
    ))
  ), [priceTypes, form.prices]);

  // Details viewda 2 ta ustun: Donaga, Optom (topilmasa first 2)
  const listPriceTypes = useMemo(() => {
    if (!priceTypes?.length) return [];
    const dona = priceTypes.find((t) => normName(t.nom).includes("dona"));
    const optom = priceTypes.find((t) => normName(t.nom).includes("optom") || normName(t.nom).includes("ulgurji"));
    const picked = [];
    if (dona) picked.push(dona);
    if (optom && optom.id !== dona?.id) picked.push(optom);
    // yetmasa: qolganlardan to‘ldiramiz
    for (const t of priceTypes) {
      if (picked.length >= 2) break;
      if (!picked.some((x) => x.id === t.id)) picked.push(t);
    }
    return picked.slice(0, 2);
  }, [priceTypes]);

  

  const getPriceByTypeName = useCallback((p, typeName) => {
    const n = (p?.narxlar || []).find((x) => String(x.sarxturi) === String(typeName));
    return n ? n.narx : "";
  }, []);

  const openQrModal = useCallback((p) => {
    // Birinchi narx turini olish: "Donaga" → bo'lmasa birinchi narx
    const narxlar = p.narxlar || [];
    const donaga = narxlar.find((n) =>
      String(n.sarxturi || "").toLowerCase().includes("dona")
    );
    const firstNarx = narxlar[0];
    const price = toNum((donaga || firstNarx)?.narx ?? 0);
    setQrModal({ series: p.seriyasi, name: p.nom, price });
  }, []);

  useEffect(() => {
    if (!qrModal || !qrCanvasRef.current) return;
    QRCode.toCanvas(qrCanvasRef.current, qrModal.series || " ", {
      width: 260,
      margin: 2,
      color: { dark: "#000000", light: "#ffffff" },
    }).catch(() => {});
  }, [qrModal]);

  const toggleView = () => setViewMode((m) => (m === "grid" ? "list" : "grid"));

  return (
    <div className="maxsulotqosh-page">

      {/* ===== MOBILE ACTION BAR ===== */}
      <div className="mob-bar">
        <button
          className="mob-bar-btn blue"
          onClick={() => { setMobileFormOpen((o) => !o); setMobileFilterOpen(false); }}
        >
          + {editingId ? "Tahrirlash" : "Qo'shish"}
        </button>
        <button
          className="mob-bar-btn"
          onClick={() => { setMobileFilterOpen((o) => !o); setMobileFormOpen(false); }}
        >
          ⚙ Filtr
        </button>
      </div>

      {mobileFormOpen && <div className="mob-bd" onClick={() => setMobileFormOpen(false)} />}
      {mobileFilterOpen && <div className="mob-bd" onClick={() => setMobileFilterOpen(false)} />}

      {/* ===== FORM SECTION ===== */}
      <div className={`mq-form-section fr-panel${mobileFormOpen ? " mob-open" : ""}`}>
        <div className="mq-sh">
          <h2>{titleText}</h2>
          <button className="mob-cls" type="button" onClick={() => setMobileFormOpen(false)}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          <ComboSelect
            value={form.brand}
            onChange={(v) => setF("brand", v)}
            items={brands}
            placeholder="Brand / Muallif"
          />

          <ComboSelect
            value={form.category}
            onChange={(v) => setF("category", v)}
            items={categories}
            placeholder="Kategoriya"
          />

          <select
            value={form.warehouse}
            disabled={!!editingId || (!allAccess && allowedIds.length === 1)}
            title={editingId ? "Skladni o'zgartirish uchun ombor ko'chirishdan foydalaning" : undefined}
            onChange={(e) => {
              const wid = e.target.value;
              const sklad = warehouses.find((x) => String(x.id) === String(wid));
              const prefix = sklad?.seriya_prefix || "km-";
              setForm((prev) => ({
                ...prev,
                warehouse: wid,
                ...(!editingId ? { series: prefix } : {}),
              }));
            }}
          >
            <option value="">Sklad tanlang</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.nom || w.name}
              </option>
            ))}
          </select>

          <input type="text" name="name" placeholder="Nomi" value={form.name} onChange={handleChange} />

          <input
            type="text"
            inputMode="decimal"
            name="quantity"
            placeholder="Soni (masalan 12.5)"
            value={form.quantity}
            onChange={handleChange}
            onBlur={(e) => setF("quantity", normDecStr(e.target.value))}
            title={editingId ? "Qoldiqni to'g'rilash. Yangi kirim uchun \"Qo'shish\" tugmasidan foydalaning" : undefined}
          />

          {!editingId && (
            <ComboSelect
              value={form.sourceSeller}
              onChange={(v) => setF("sourceSeller", v)}
              items={sellers}
              placeholder="Kimdan kelgani (ixtiyoriy)"
              getLabel={(s) =>
                s
                  ? `${(s.ismi || "").trim() || "Nomsiz"}${s.Dokonnomi ? ` - ${s.Dokonnomi}` : ""}`
                  : ""
              }
            />
          )}

          <input
            type="text"
            inputMode="decimal"
            name="costPrice"
            placeholder="Tan narxi (so'm, masalan 18000)"
            value={form.costPrice}
            onChange={handleChange}
            onBlur={(e) => setF("costPrice", trimTrailingZeros(e.target.value))}
          />

          <input type="text" name="series" placeholder="Seriyasi" value={form.series} onChange={handleChange} />

          <input
            type="text"
            name="qrKod"
            placeholder="QR / Shtrix kodi — bir nechtasini bo'sh joy bilan ajrating (ixtiyoriy)"
            value={form.qrKod}
            onChange={handleChange}
            maxLength={200}
          />

          <input type="text" name="description" placeholder="Izoh (ixtiyoriy)" value={form.description} onChange={handleChange} />

          {editingId && (
            <input
              type="text"
              name="editNote"
              placeholder="Tahrirlash izohi (ma'lumot o'zgarsa majburiy)"
              value={form.editNote}
              onChange={handleChange}
            />
          )}

          <div className="image-upload">
            <label className="upload-btn">
              Fayldan yuklash
              <input type="file" accept="image/*" onChange={handleImageChange} hidden />
            </label>
            {form.imagePreview && (
              <img src={form.imagePreview} alt="Preview" className="preview-img" />
            )}
          </div>

          <h3>Narxlar turlari</h3>
          {priceInputs}

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="submit" className="add-btn">
              {editingId ? "Saqlash" : "Qo‘shish"}
            </button>
            {editingId && (
              <button type="button" className="add-btn" onClick={resetForm}>
                Bekor qilish
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ===== FILTER SECTION ===== */}
      <div className={`mq-filter-section fr-panel${mobileFilterOpen ? " mob-open" : ""}`}>
        <div className="mq-sh">
          <h3>Filtr & Qidiruv</h3>
          <button className="mob-cls" type="button" onClick={() => setMobileFilterOpen(false)}>×</button>
        </div>
        <div className="toolbar">
          <input
            className="search-input"
            placeholder="Nomi yoki seriya..."
            value={searchText}
            onChange={handleSearchChange}
          />

          <select value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
            <option value="all">Sklad: Hammasi</option>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.nom || w.name}</option>
            ))}
          </select>

          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">Kategoriya: Barchasi</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.nom || c.name}</option>
            ))}
          </select>

          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="all">Brand: Barchasi</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.nom || b.name}</option>
            ))}
          </select>

          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}>
            <option value="available">Qolgan</option>
            <option value="out">Qolmagan</option>
            <option value="all">Barchasi</option>
          </select>

          <select value={sortSeries} onChange={(e) => setSortSeries(e.target.value)}>
            <option value="asc">Seriya: Osish</option>
            <option value="desc">Seriya: Kamayish</option>
          </select>

          <button type="button" className="view-btn" onClick={toggleView} title="Ko’rinishni o’zgartirish">
            {viewMode === "grid" ? "Details" : "Grid"}
          </button>
        </div>
      </div>

      {/* ===== LIST SECTION ===== */}
      <div className="form-container fr-panel">
        <h3 className="list-title">
          Mahsulotlar ro‘yxati
          <span style={{ marginLeft: 10, fontSize: 12, opacity: 0.7 }}>
            (Ko‘rsatilmoqda: {items.length} / {allRef.current.length})
          </span>
        </h3>

        {/* ===== GRID VIEW ===== */}
        {viewMode === "grid" && (
          <div className="product-grid">
            {items.map((p) => (
              <div key={p.id} className="product-card fr-card">
                <div className="pc-left">
                  <img className="pc-img" src={imgSrc(p)} alt={p.nom || ""} loading="lazy" decoding="async" onError={handleImgError} />
                  <button type="button" className="edit-btn block" onClick={() => editProduct(p)}>Tahrirlash</button>
                  <button type="button" className="add-stock-btn block" onClick={() => openAddModal(p)}>Qo’shish</button>
                  <button type="button" className="edit-btn block" onClick={() => openImagePicker(p.id)}>Rasmni almashtirish</button>
                  <button type="button" className="qr-btn block" onClick={() => openQrModal(p)}>QR kod</button>
                </div>

                <div className="pc-body">
                  <div className="pc-row"><b>Nomi:</b> {p.nom}</div>
                  <div className="pc-row"><b>Soni:</b> {fmtQty(p.soni)} dona</div>
                  <div className="pc-row"><b>Seriyasi:</b> {p.seriyasi}</div>
                  <div className="pc-row"><b>Tan narxi:</b> {fmtMoney(p.tan_narxi)}</div>

                  {p.kategoriya_nom && <div className="pc-row"><b>Kategoriya:</b> {p.kategoriya_nom}</div>}
                  {p.brand_nom && <div className="pc-row"><b>Brand:</b> {p.brand_nom}</div>}
                  {p.sklad_nom && <div className="pc-row"><b>Sklad:</b> {p.sklad_nom}</div>}

                  <div className="pc-prices">
                    {(p.narxlar || []).map((n) => (
                      <div className="price-chip" key={n.id}>
                        <span className="label">{n.sarxturi}</span>
                        <span className="value">{fmtMoney(n.narx)}</span>
                        <button type="button" className="chip-btn" onClick={() => handleEditPrice(p.id, n)} title="Tahrirlash">✎</button>
                        <button type="button" className="chip-btn" onClick={() => handleDeletePrice(p.id, n.id)} title="O‘chirish">🗑</button>
                      </div>
                    ))}
                  </div>

                  <div className="pc-actions">
                    <button type="button" className="delete-btn" onClick={() => handleDelete(p.id)}>O‘chirish</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== LIST/DETAILS VIEW ===== */}
        {viewMode === "list" && (
          <div className="product-list">
            <div className="list-header">
              <div className="col img"></div>
              <div className="col series">seriyasi</div>
              <div className="col name">nomi</div>
              <div className="col qty">soni</div>
              <div className="col cost">tan narxi</div>

              {listPriceTypes.map((t) => (
                <div className="col price" key={t.id}>{String(t.nom || "").toLowerCase()}</div>
              ))}

              <div className="col cat">kategoriya</div>
              <div className="col brand">brand</div>
              <div className="col wh">sklad</div>
              <div className="col act">amallar</div>
            </div>

            {items.map((p) => (
              <div className="list-row" key={p.id}>
                <div className="col img">
                  <img className="li-img" src={imgSrc(p)} alt={p.nom || ""} loading="lazy" decoding="async" onError={handleImgError} />
                </div>

                <div className="col series">{p.seriyasi}</div>

                <div className="col name">
                  <div className="li-strong">{p.nom}</div>
                </div>

                <div className="col qty">{fmtQty(p.soni)}</div>
                <div className="col cost">{fmtMoney(p.tan_narxi)}</div>

                {listPriceTypes.map((t) => (
                  <div className="col price" key={t.id}>
                    {getPriceByTypeName(p, t.nom) !== ""
                      ? fmtMoney(getPriceByTypeName(p, t.nom))
                      : ""}
                  </div>
                ))}

                <div className="col cat">{p.kategoriya_nom || ""}</div>
                <div className="col brand">{p.brand_nom || ""}</div>
                <div className="col wh">{p.sklad_nom || ""}</div>

                <div className="col act">
                  <div className="li-actions">
                    <button type="button" className="edit-btn" onClick={() => editProduct(p)}>Tahrirlash</button>
                    <button type="button" className="add-stock-btn" onClick={() => openAddModal(p)}>Qo’shish</button>
                    <button type="button" className="edit-btn" onClick={() => openImagePicker(p.id)}>Rasm</button>
                    <button type="button" className="qr-btn" onClick={() => openQrModal(p)}>QR</button>
                    <button type="button" className="delete-btn" onClick={() => handleDelete(p.id)}>O’chirish</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && <p className="loading">Yuklanmoqda...</p>}
        <div ref={loaderRef} style={{ height: 1 }} />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={onPickNewImage}
          style={{ display: "none" }}
        />

        {addModalOpen && addTarget && (
          <div className="modal-backdrop" onClick={closeAddModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <div className="modal-title">Mahsulot qo‘shish</div>
                <button className="modal-close" onClick={closeAddModal} disabled={addSubmitting}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-row">
                  <div className="modal-label">Mahsulot</div>
                  <div className="modal-value">
                    {addTarget.nom} {addTarget.seriyasi ? `(${addTarget.seriyasi})` : ""}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Soni (butun)"
                  value={addQty}
                  onChange={(e) => setAddQty(normDecStr(e.target.value))}
                />
                <SellerSelect
                  value={addSeller}
                  onChange={(v) => setAddSeller(v)}
                  sellers={sellers}
                  placeholder="Kimdan kelgani (majburiy)"
                />
                <input
                  type="text"
                  placeholder="Izoh (ixtiyoriy)"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                />
              </div>
              <div className="modal-foot">
                <button className="edit-btn" onClick={closeAddModal} disabled={addSubmitting}>
                  Bekor qilish
                </button>
                <button className="add-btn" onClick={submitAddStock} disabled={addSubmitting}>
                  {addSubmitting ? "Saqlanmoqda..." : "Qo‘shish"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== QR KOD MODAL ===== */}
      {qrModal && (
        <div className="modal-backdrop" onClick={() => setQrModal(null)}>
          <div className="modal qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">QR Kod cheki</div>
              <button className="modal-close" onClick={() => setQrModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Chek preview — chop etilgandagi ko'rinish */}
              <div className="qr-ticket-preview">
                <div className="qr-ticket-top">
                  <canvas ref={qrCanvasRef} className="qr-ticket-canvas" />
                  <div className="qr-ticket-info">
                    <div className="qr-ticket-series">{qrModal.series}</div>
                    {qrModal.price > 0 && (
                      <div className="qr-ticket-price">
                        {Math.round(qrModal.price).toLocaleString("uz-UZ")}
                      </div>
                    )}
                  </div>
                </div>
                {qrModal.name && (
                  <div className="qr-ticket-name">{qrModal.name}</div>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="edit-btn" onClick={() => setQrModal(null)}>Yopish</button>
              <button
                className="edit-btn"
                onClick={() => downloadQrPdf(qrModal.series, qrModal.name, qrModal.price)}
              >
                ⬇ PDF yuklab olish
              </button>
              <button
                className="add-btn"
                onClick={() => printQrLabel(qrModal.series, qrModal.name, qrModal.price)}
              >
                🖨 Chop etish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
